import { Capacitor, registerPlugin } from '@capacitor/core'
import { initialLocale } from '@/lib/locale'

// « Vitesse en cabine » — the WebView→native bridge. Three fire-and-forget
// signals, nothing more: the car screen's DATA comes from the native side's
// own /api/car_stops fetch (one derivation, server-owned), never from payloads
// pushed across this bridge.
//
//  - setActiveContext: mirrors the active org + locale into native prefs —
//    the org id lives in WebView localStorage, which native cannot read.
//  - stopResolved: a visit verb just landed a terminal outcome. The native
//    side refetches the feed and, if stops remain, may surface the
//    zero-gesture-return notification. Carries NO payload on purpose.
//  - refreshHint: something about today moved (cable ripple, close-day) —
//    refetch when convenient. Never a notification.
//
// Android-only: CarPlay (1B) is store-gated, and web/iOS calls are no-ops.
// Every call swallows errors — a missing native implementation must never
// break a phone flow.

interface VitesseCarPlugin {
  setActiveContext(options: { orgId: string | null; locale: string; apiBase: string }): Promise<void>
  stopResolved(): Promise<void>
  refreshHint(): Promise<void>
}

const plugin = registerPlugin<VitesseCarPlugin>('VitesseCar')

const fire = (call: () => Promise<void>) => {
  if (Capacitor.getPlatform() !== 'android') return
  call().catch(() => { /* the bridge is best-effort by design */ })
}

export const carBridge = {
  setActiveContext(orgId: string | null) {
    fire(() => plugin.setActiveContext({
      orgId,
      locale: initialLocale(),
      // Same base resolution as api/client.ts and the cable: the native car
      // service must call the exact host this build talks to.
      apiBase: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000',
    }))
  },
  stopResolved() {
    fire(() => plugin.stopResolved())
  },
  refreshHint() {
    fire(() => plugin.refreshHint())
  },
}
